import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import emailChanges from '../../../../data/emailChanges';
import { timestampToDate } from '../../../../utils/firebase-timestamp-transformation';
import './TaskAttachmentsTab.scss';

const TaskAttachmentsTab = () => {
  const [taskAttachments, setTaskAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [migratedCount, setMigratedCount] = useState(0);

  useEffect(() => {
    fetchTaskAttachments();
  }, []);

  const fetchTaskAttachments = async () => {
    try {
      setLoading(true);
      
      // Access the taskAttachments subcollection
      const taskAttachmentsDocRef = doc(db, 'otherDBs', 'taskAttachments');
      const taskAttachmentsCollectionRef = collection(taskAttachmentsDocRef, 'taskAttachments');
      const querySnapshot = await getDocs(taskAttachmentsCollectionRef);
      
      const attachmentsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTaskAttachments(attachmentsList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching task attachments:', error);
      setLoading(false);
    }
  };

  // Function to find new email for an employee from emailChanges.js
  const findNewEmail = (oldEmail) => {
    const change = emailChanges.find(change => change.oldEmail === oldEmail);
    return change ? change.newEmail : null;
  };

  // Handle migrate all attachments
  const handleMigrateAll = async () => {
    if (!window.confirm(`Are you sure you want to migrate all attachments? This will update ${taskAttachments.length} records.`)) {
      return;
    }
    
    try {
      setMigrating(true);
      
      // Find attachments that need migration
      const attachmentsToMigrate = taskAttachments.filter(attachment => {
        // Skip already migrated attachments
        if (attachment.migrated) return false;
        
        // Skip if uploadedBy doesn't exist or has no mapping
        return attachment.uploadedBy && findNewEmail(attachment.uploadedBy);
      });
      
      setProgress({ current: 0, total: attachmentsToMigrate.length });
      
      // Initialize batch
      let batch = writeBatch(db);
      let batchCount = 0;
      let totalMigrated = 0;
      
      // Process attachments in batches (Firestore has a limit of 500 operations per batch)
      for (let i = 0; i < attachmentsToMigrate.length; i++) {
        const attachment = attachmentsToMigrate[i];
        const oldEmail = attachment.uploadedBy;
        const newEmail = findNewEmail(oldEmail);
        
        if (newEmail) {
          const attachmentRef = doc(db, 'otherDBs', 'taskAttachments', 'taskAttachments', attachment.id);
          
          batch.update(attachmentRef, {
            uploadedBy: newEmail,
            oldEmail: oldEmail,
            migrated: true,
            migratedAt: new Date()
          });
          
          batchCount++;
          totalMigrated++;
          
          // If we've reached the batch limit, commit and start a new batch
          if (batchCount >= 450) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
        }
        
        // Update progress
        setProgress({ current: i + 1, total: attachmentsToMigrate.length });
      }
      
      // Commit any remaining updates
      if (batchCount > 0) {
        await batch.commit();
      }
      
      setMigratedCount(totalMigrated);
      alert(`Successfully migrated ${totalMigrated} task attachments.`);
      
      // Refresh the list
      fetchTaskAttachments();
      
    } catch (error) {
      console.error('Error migrating task attachments:', error);
      alert(`Error migrating task attachments: ${error.message}`);
    } finally {
      setMigrating(false);
    }
  };

  const filteredAttachments = searchTerm 
    ? taskAttachments.filter(attachment => 
        attachment.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        attachment.uploadedBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        attachment.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        attachment.taskID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        attachment.file?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : taskAttachments;

  // Format date for display using the utility function
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const convertedDate = timestampToDate(date);
    return convertedDate ? convertedDate.toLocaleDateString() : 'N/A';
  };

  // Get filename from URL
  const getFileName = (fileUrl) => {
    if (!fileUrl) return 'N/A';
    try {
      // Extract filename from Firebase Storage URL
      const urlParts = fileUrl.split('/');
      const fileWithParams = urlParts[urlParts.length - 1];
      const fileName = fileWithParams.split('?')[0];
      // Decode URI component to handle special characters
      return decodeURIComponent(fileName);
    } catch (error) {
      return fileUrl.substring(fileUrl.lastIndexOf('/') + 1, fileUrl.indexOf('?'));
    }
  };

  // Count total number of migratable attachments
  const getMigratableCounts = () => {
    const total = taskAttachments.length;
    const alreadyMigrated = taskAttachments.filter(attachment => attachment.migrated).length;
    const canBeMigrated = taskAttachments.filter(attachment => 
      !attachment.migrated && 
      attachment.uploadedBy && 
      findNewEmail(attachment.uploadedBy)
    ).length;
    const noMapping = total - alreadyMigrated - canBeMigrated;
    
    return { total, alreadyMigrated, canBeMigrated, noMapping };
  };

  const counts = getMigratableCounts();

  return (
    <div className="task-attachments-tab">
      <div className="attachments-header">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by email, title, or task ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="refresh-container">
          <button 
            className="refresh-button"
            onClick={fetchTaskAttachments}
            disabled={loading || migrating}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      <div className="migration-control">
        <div className="stats">
          <div className="stat-item">
            <span className="stat-label">Total Attachments:</span>
            <span className="stat-value">{counts.total}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Already Migrated:</span>
            <span className="stat-value">{counts.alreadyMigrated}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Ready to Migrate:</span>
            <span className="stat-value">{counts.canBeMigrated}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">No Email Mapping:</span>
            <span className="stat-value">{counts.noMapping}</span>
          </div>
        </div>
        
        <button 
          className="migrate-all-button"
          onClick={handleMigrateAll}
          disabled={loading || migrating || counts.canBeMigrated === 0}
        >
          {migrating ? 'Migrating...' : 'Migrate All Attachments'}
        </button>
      </div>
      
      {migrating && (
        <div className="progress-bar-container">
          <div className="progress-text">
            Migrating {progress.current} of {progress.total} attachments...
          </div>
          <div className="progress-bar-wrapper">
            <div 
              className="progress-bar" 
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
      
      {migratedCount > 0 && !migrating && (
        <div className="success-message">
          Successfully migrated {migratedCount} attachments in the last operation.
        </div>
      )}
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading task attachments...</p>
        </div>
      ) : filteredAttachments.length > 0 ? (
        <div className="attachments-table-container">
          <table className="attachments-table">
            <thead>
              <tr>
                <th>Task ID</th>
                <th>Title</th>
                <th>File</th>
                <th>Uploaded By</th>
                <th>Created At</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredAttachments.map(attachment => (
                <tr key={attachment.id} className={attachment.migrated ? 'migrated-row' : ''}>
                  <td>{attachment.taskID || 'N/A'}</td>
                  <td>{attachment.title || 'N/A'}</td>
                  <td className="file-cell">
                    {attachment.file ? (
                      <a href={attachment.file} target="_blank" rel="noopener noreferrer">
                        {getFileName(attachment.file)}
                      </a>
                    ) : 'N/A'}
                  </td>
                  <td>
                    {attachment.uploadedBy || 'N/A'}
                    {attachment.migrated && (
                      <div>
                        <span className="migrated-badge">Migrated</span>
                        <div className="old-email">Old: {attachment.oldEmail}</div>
                      </div>
                    )}
                  </td>
                  <td>{formatDate(attachment.createdAt)}</td>
                  <td>
                    {attachment.migrated ? (
                      <span className="already-migrated">Migrated</span>
                    ) : findNewEmail(attachment.uploadedBy) ? (
                      <span className="can-migrate">Ready to Migrate</span>
                    ) : (
                      <span className="no-mapping">No Mapping</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">
          <p>No task attachments found.</p>
        </div>
      )}
    </div>
  );
};

export default TaskAttachmentsTab; 